import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  LEVEL_NAMES,
  analyticsSummarySchema,
  bestPerRegionListSchema,
  countryListSchema,
  countryOpportunityListSchema,
  imageStatListSchema,
  potentialListSchema,
  rateListSchema,
  retryListSchema,
  regionListSchema,
  type Level,
} from '@competa/shared';
import { apiUrl } from '../../api/client';
import { ImageViewer } from '../../components/ImageViewer';
import viewerStyles from '../../components/ImageViewer.module.css';
import { formatDate } from '../salons/SalonSearchPage';
import { errorMessage } from '../../i18n/errors';
import styles from './Analytics.module.css';
import { useAnalytics, type Topic } from './queries';

/** Zeilen je Karte (der Rest steht auf der Detailseite). */
const CARD_ROWS = 5;

/**
 * Karte des Dashboards mit Überschrift, Inhalt und Link zur Detailseite.
 * @param props Thema, Titel und Inhalt.
 */
function Card({
  topic,
  title,
  isEmpty,
  children,
}: {
  topic: Topic;
  title: string;
  isEmpty: boolean;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <section className={styles.card} aria-label={title}>
      <h2>{title}</h2>
      {isEmpty ? <p>{t('analytics.empty')}</p> : children}
      <p>
        <Link to={`/analytics/${topic}`}>{t('analytics.showAll')}</Link>
      </p>
    </section>
  );
}

/**
 * Bild-Zeile mit Vorschau, Titel und Wert.
 * @param props Bild-ID, Titel und Wert.
 */
export function ImageRow({
  imageId,
  title,
  subtitle,
  detail,
  value,
  onOpen,
}: {
  imageId: number;
  title: string;
  /** Optionale zweite Zeile unter dem Titel (z. B. die Region). */
  subtitle?: ReactNode;
  /** Optionale dritte Zeile (Einzelheiten), z. B. Quote und Einsendeschluss. */
  detail?: string;
  /** Wert rechts neben dem Titel (entfällt, wenn die Einzelheiten in der dritten Zeile stehen). */
  value?: string;
  /** Öffnet die große Bildansicht (Klick auf das Vorschaubild). */
  onOpen?: () => void;
}) {
  const { t } = useTranslation();
  const thumb = (
    <img className={styles.thumb} src={apiUrl(`/images/${imageId}/thumb`)} alt="" loading="lazy" />
  );
  return (
    <li>
      <span className={styles.rowTitle}>
        {onOpen ? (
          <button
            type="button"
            className={viewerStyles.thumbButton}
            aria-label={t('viewer.open', { name: title })}
            onClick={onOpen}
          >
            {thumb}
          </button>
        ) : (
          thumb
        )}
        <span className={styles.rowText}>
          {title}
          {subtitle && <span className={styles.rowSub}>{subtitle}</span>}
          {detail && <span className={styles.rowDetail}>{detail}</span>}
        </span>
      </span>
      {value && <span className={styles.value}>{value}</span>}
    </li>
  );
}

/** Eine Zeile einer Bildliste. */
interface ImageRowData {
  key: string | number;
  imageId: number;
  title: string;
  subtitle?: ReactNode;
  detail?: string;
  value?: string;
}

/**
 * Liste von Bildzeilen mit großer Bildansicht: Ein Klick auf ein Vorschaubild öffnet das Bild, blättern geht
 * innerhalb dieser Liste.
 * @param props Zeilen der Liste.
 */
function ImageRows({ rows }: { rows: ImageRowData[] }) {
  const [index, setIndex] = useState<number | null>(null);
  return (
    <>
      <ul className={styles.rows}>
        {rows.map((row, position) => (
          <ImageRow
            key={row.key}
            imageId={row.imageId}
            title={row.title}
            subtitle={row.subtitle}
            detail={row.detail}
            value={row.value}
            onOpen={() => setIndex(position)}
          />
        ))}
      </ul>
      <ImageViewer
        items={rows.map((row) => ({ id: row.imageId, title: row.title }))}
        index={index}
        onIndexChange={setIndex}
        onClose={() => setIndex(null)}
      />
    </>
  );
}

/**
 * Dashboard: Stufenfortschritt (NFIAP bis EFIAP/p) mit Vorschlag und Karten für Top-Bilder, Überlauf, Potenzial,
 * Flops, Länder, Regionen mit Erfolgsquote (Misserfolge zählen mit), bestes Bild je Region und Kategorien.
 */
export function Dashboard() {
  const { t, i18n } = useTranslation();
  const summary = useAnalytics('/summary', analyticsSummarySchema);
  const top = useAnalytics('/top-images?limit=5', imageStatListSchema);
  const over = useAnalytics('/overlimit', imageStatListSchema);
  const potential = useAnalytics('/potential?limit=5', potentialListSchema);
  const retry = useAnalytics('/retry-suggestions?limit=5', retryListSchema);
  const countries = useAnalytics('/countries', countryListSchema);
  const regions = useAnalytics('/regions', regionListSchema);
  const best = useAnalytics('/best-per-region', bestPerRegionListSchema);
  const categories = useAnalytics('/categories', rateListSchema);
  const opportunities = useAnalytics(
    '/country-opportunities?limit=5',
    countryOpportunityListSchema,
  );
  const [chosen, setChosen] = useState<Level | null>(null);

  if (summary.isPending) return <p role="status">{t('common.loading')}</p>;
  if (summary.isError) {
    return (
      <p role="alert" className={styles.error}>
        {errorMessage(t, summary.error)}
      </p>
    );
  }
  const data = summary.data;
  const level = chosen ?? data.suggested ?? 'EFIAP_p';
  const current = data.levels.find((row) => row.level === level)!;
  /** Formatiert ein Verhältnis als Prozent. */
  const percent = (value: number) => `${Math.round(value * 100)} %`;

  return (
    <div>
      <h2>{t('analytics.progressTitle')}</h2>
      <div className={styles.levelBar}>
        <div className={styles.selectField}>
          <label htmlFor="level-select">{t('analytics.level')}</label>
          <select
            id="level-select"
            className={styles.select}
            value={level}
            onChange={(event) => setChosen(event.target.value as Level)}
          >
            {LEVEL_NAMES.map((name) => (
              <option key={name} value={name}>
                {t(`analytics.levels.${name}`)}
              </option>
            ))}
          </select>
        </div>
        {data.suggested ? (
          <p>{t('analytics.suggested', { level: t(`analytics.levels.${data.suggested}`) })}</p>
        ) : (
          <p className={styles.fulfilled}>{t('analytics.allDone')}</p>
        )}
      </div>

      <dl className={styles.stats}>
        {(
          [
            ['acceptances', data.stats.acceptances],
            ['images', data.stats.images],
            ['countries', data.stats.countries],
            ['awards', data.stats.awards],
            ['overflow', data.stats.overflow],
          ] as const
        ).map(([key, value]) => (
          <div key={key} className={styles.stat}>
            <dd className={styles.statValue}>{value}</dd>
            <dt className={styles.statLabel}>{t(`analytics.stat.${key}`)}</dt>
          </div>
        ))}
      </dl>

      <div
        aria-label={t('analytics.progressFor', { level: t(`analytics.levels.${level}`) })}
        role="group"
      >
        {(['acceptances', 'images', 'countries'] as const).map((key) => (
          <div key={key} className={styles.progressRow}>
            <label htmlFor={`progress-${key}`}>{t(`analytics.stat.${key}`)}</label>
            <progress
              id={`progress-${key}`}
              className={styles.progress}
              max={100}
              value={Math.round(current.progress[key] * 100)}
            />
            <span className={styles.progressValue}>
              {data.stats[key]} / {current.requirement[key]} ({percent(current.progress[key])})
            </span>
          </div>
        ))}
        {current.fulfilled && <p className={styles.fulfilled}>{t('analytics.fulfilled')}</p>}
      </div>

      <div className={styles.cards}>
        <Card topic="top-images" title={t('analytics.topImages')} isEmpty={!top.data?.items.length}>
          <ImageRows
            rows={(top.data?.items ?? []).slice(0, CARD_ROWS).map((row) => ({
              key: row.imageId,
              imageId: row.imageId,
              title: row.title,
              value: t('analytics.acceptancesCount', { count: row.acceptances }),
            }))}
          />
        </Card>
        <Card topic="overlimit" title={t('analytics.overlimit')} isEmpty={!over.data?.items.length}>
          <ImageRows
            rows={(over.data?.items ?? []).slice(0, CARD_ROWS).map((row) => ({
              key: row.imageId,
              imageId: row.imageId,
              title: row.title,
              value: t('analytics.overflowCount', { count: row.overflow }),
            }))}
          />
        </Card>
        <Card
          topic="potential"
          title={t('analytics.potential')}
          isEmpty={!potential.data?.items.length}
        >
          <ImageRows
            rows={(potential.data?.items ?? []).slice(0, CARD_ROWS).map((row) => ({
              key: row.imageId,
              imageId: row.imageId,
              title: row.title,
              value: t('analytics.potentialCount', { count: row.potential }),
            }))}
          />
        </Card>
        <Card
          topic="retry-suggestions"
          title={t('analytics.retry')}
          isEmpty={!retry.data?.items.length}
        >
          <ImageRows
            rows={(retry.data?.items ?? []).slice(0, CARD_ROWS).map((row) => ({
              key: row.imageId,
              imageId: row.imageId,
              title: row.title,
              subtitle: (
                <Link to={`/competitions/new?q=${encodeURIComponent(row.salon.fiapNumber)}`}>
                  {row.salon.name}
                </Link>
              ),
              detail: [
                t('analytics.retryRate', {
                  rate: row.rate,
                  accepted: row.accepted,
                  decided: row.decided,
                }),
                row.newCountry ? t('analytics.retryNewCountry') : null,
                t('analytics.retryUntil', {
                  date: formatDate(row.salon.closingDate, i18n.language),
                }),
              ]
                .filter(Boolean)
                .join(' · '),
            }))}
          />
        </Card>
        <Card
          topic="countries"
          title={t('analytics.countries')}
          isEmpty={!countries.data?.items.length}
        >
          <ul className={styles.rows}>
            {countries.data?.items.slice(0, CARD_ROWS).map((row) => (
              <li key={row.country}>
                <span>{row.country}</span>
                <span className={styles.value}>
                  {t('analytics.acceptancesCount', { count: row.acceptances })}
                </span>
              </li>
            ))}
          </ul>
        </Card>
        <Card topic="regions" title={t('analytics.regions')} isEmpty={!regions.data?.items.length}>
          <ul className={styles.rows}>
            {regions.data?.items.slice(0, CARD_ROWS).map((row) => (
              <li key={row.key}>
                <span>{row.key}</span>
                <span className={styles.value}>
                  {t('analytics.rate', {
                    rate: row.rate,
                    accepted: row.accepted,
                    decided: row.decided,
                  })}
                </span>
              </li>
            ))}
          </ul>
        </Card>
        <Card
          topic="best-per-region"
          title={t('analytics.bestPerRegion')}
          isEmpty={!best.data?.items.length}
        >
          <ImageRows
            rows={(best.data?.items ?? []).slice(0, CARD_ROWS).map((row) => ({
              key: row.region,
              imageId: row.imageId,
              title: row.title,
              subtitle: row.region,
              value: t('analytics.acceptancesCount', { count: row.acceptances }),
            }))}
          />
        </Card>
        <Card
          topic="categories"
          title={t('analytics.categories')}
          isEmpty={!categories.data?.items.length}
        >
          <ul className={styles.rows}>
            {categories.data?.items.slice(0, CARD_ROWS).map((row) => (
              <li key={row.key}>
                <span>{row.key}</span>
                <span className={styles.value}>
                  {t('analytics.rate', {
                    rate: row.rate,
                    accepted: row.accepted,
                    decided: row.decided,
                  })}
                </span>
              </li>
            ))}
          </ul>
        </Card>
        <Card
          topic="country-opportunities"
          title={t('analytics.opportunities')}
          isEmpty={!opportunities.data?.items.length}
        >
          <ul className={styles.rows}>
            {opportunities.data?.items.slice(0, CARD_ROWS).map((row) => (
              <li key={row.country}>
                <span className={styles.rowText}>
                  <Link to={`/competitions/new?country=${encodeURIComponent(row.country)}`}>
                    {row.country}
                  </Link>
                  <span className={styles.rowSub}>
                    {t('analytics.opportunityNext', {
                      date: formatDate(row.nextClosing, i18n.language),
                    })}
                  </span>
                </span>
                <span className={styles.value}>
                  {t('analytics.opportunityOpen', { count: row.openSalons })}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      </div>
      <p className={styles.moreLinks}>
        <Link to="/analytics/country-gaps">{t('analytics.countryGaps')}</Link>
        <Link to="/analytics/flops">{t('analytics.flopsLink')}</Link>
      </p>
    </div>
  );
}
